/**
 * A5-M7 (Welle 2): Vorgangskennung je Kassenbuch-Buchung (Kassasturz, Trinkgeld-Entnahme).
 *
 * Jede neue Buchung bekommt eine neue `vorgangId` – unabhängig vom Inhalt (Codex W2-A2 R1: zwei
 * gleiche Zählungen sind zwei Buchungen). Sie wird **vor** dem Senden dauerhaft gemerkt
 * (`localStorage`, je Kasse); ohne beschreibbaren Speicher wird nicht gesendet (Codex R2).
 *
 * Geht die Antwort verloren (Netzfehler, Zeitüberschreitung, 5xx), ist offen, ob der Server
 * gebucht hat: Die Buchung bleibt **ungeklärt** (übersteht Reiterwechsel und Neuladen). Nur das
 * ausdrückliche „erneut senden“ verwendet Kennung und Inhalt unverändert wieder; ein Server mit
 * Vorgangsprüfung bucht dann nicht ein zweites Mal. Ein Server ohne diese Prüfung ignoriert das
 * Feld (das Zod-Schema der Route entfernt unbekannte Felder) – dort schützt nur die Oberfläche.
 * Solange eine Buchung ungeklärt ist oder läuft, nimmt der Abschluss keine neue an.
 *
 * Laufende Buchungen und ihr Ausgang stehen zusätzlich in einem Sitzungsregister je Kasse
 * (Modulebene, nicht in der Komponente – Codex R2): Wird der Reiter während der Anfrage
 * gewechselt, übernimmt die neue Ansicht die laufende Anfrage, statt sie als ungeklärt
 * anzubieten. Das Register ist für diese Seite maßgeblich, der Speicher für das Neuladen.
 */
import { ApiFehler, type KassenbuchArt } from './api';
import { neueVorgangsId } from './bonierung';

interface Speicher {
  getItem(schluessel: string): string | null;
  setItem(schluessel: string, wert: string): void;
  removeItem(schluessel: string): void;
}

/** Kassenbuch-Buchung mit Kennung – vor dem Senden gemerkt, bis ihr Ausgang feststeht. */
export interface KassenbuchVorgang {
  vorgangId: string;
  art: Extract<KassenbuchArt, 'zaehlung' | 'entnahme'>;
  betragCent: number;
  grund: string;
  /** Erster Sendeversuch (ISO). */
  seit: string;
}

/**
 * Wiederholversand einer ungeklärten Buchung. Eingeschaltet mit E-W1-RKSV F2 (Nutzer 27.10.2026),
 * zusammen mit der Server-Idempotenz 17f2d82 (A5-M7).
 *
 * Rollback-Untergrenze technisch: Auch bei eingeschaltetem Schalter geht eine Wiederholung nur
 * hinaus, wenn der Server unmittelbar davor ausdrücklich meldet, dass er Wiederholungen derselben
 * Kennung erkennt (`faehigkeiten.kassenbuchVorgang`). Ein auf einen Stand vor 17f2d82
 * zurückgesetzter Server meldet das nicht – dann bleibt es beim Prüfen im Kassenbuch und Verwerfen
 * des Merkers (Codex W2-A2 R4: der alte Server bucht eine Wiederholung neu).
 * Veränderbares Objekt nur, damit Tests beide Stellungen prüfen können.
 */
export const KASSENBUCH_SCHALTER: { wiederholungAktiv: boolean } = { wiederholungAktiv: true };

/** Wiederholung einer bekannten (ungeklärten bzw. erledigten) Buchung bei ausgeschaltetem Schalter. */
export class KassenbuchWiederholungGesperrt extends Error {
  override readonly name = 'KassenbuchWiederholungGesperrt';

  constructor() {
    super(
      'Erneutes Senden ist gesperrt, bis der Server Wiederholungen erkennt – im Kassenbuch prüfen, anschließend den Merker verwerfen',
    );
  }
}

/** Gerätespeicher nicht beschreibbar: Die Buchung wurde nicht gesendet. */
export class KassenbuchSpeicherFehler extends Error {
  override readonly name = 'KassenbuchSpeicherFehler';

  constructor() {
    super(
      'Gerätespeicher nicht beschreibbar – Buchung nicht gesendet (ohne Merker wäre eine Wiederholung nach verlorener Antwort nicht erkennbar)',
    );
  }
}

/** Sitzungsregister je Kasse: laufende Anfrage und ungeklärte Buchung dieser Seite. */
const sitzung = new Map<
  string,
  { lauf: Promise<void> | null; ungeklaert: KassenbuchVorgang | null | undefined }
>();

function eintrag(kassenId: string) {
  let e = sitzung.get(kassenId);
  if (e === undefined) {
    e = { lauf: null, ungeklaert: undefined };
    sitzung.set(kassenId, e);
  }
  return e;
}

/** Nur für Tests: Seitenwechsel bzw. Neuladen nachstellen (Register leer, Speicher bleibt). */
export function kassenbuchSitzungZuruecksetzen(): void {
  sitzung.clear();
}

function schluessel(kassenId: string): string {
  return `bilanx.kassenbuch.vorgang.${kassenId}`;
}

/** Neue Buchung: immer eine neue Kennung. */
export function neuerKassenbuchVorgang(
  e: Pick<KassenbuchVorgang, 'art' | 'betragCent' | 'grund'>,
  jetzt: Date = new Date(),
): KassenbuchVorgang {
  return {
    vorgangId: neueVorgangsId(),
    art: e.art,
    betragCent: e.betragCent,
    grund: e.grund,
    seit: jetzt.toISOString(),
  };
}

/**
 * Gemerkte ungeklärte Buchung laut Gerätespeicher (oder `null`). Unlesbare Daten und die
 * Erledigt-Markierung gelten als keine.
 */
export function ladeUngeklaerteBuchung(
  speicher: Speicher,
  kassenId: string,
): KassenbuchVorgang | null {
  try {
    const roh = speicher.getItem(schluessel(kassenId));
    if (roh === null || roh === '') {
      return null;
    }
    const m = JSON.parse(roh) as Record<string, unknown> | null;
    if (
      typeof m === 'object' &&
      m !== null &&
      m.erledigt === undefined &&
      typeof m.vorgangId === 'string' &&
      (m.art === 'zaehlung' || m.art === 'entnahme') &&
      typeof m.betragCent === 'number' &&
      typeof m.grund === 'string' &&
      typeof m.seit === 'string'
    ) {
      return {
        vorgangId: m.vorgangId,
        art: m.art,
        betragCent: m.betragCent,
        grund: m.grund,
        seit: m.seit,
      };
    }
    return null;
  } catch {
    return null;
  }
}

/** Ungeklärte Buchung dieser Kasse: zuerst das Sitzungsregister, sonst der Gerätespeicher. */
export function ungeklaerteBuchung(speicher: Speicher, kassenId: string): KassenbuchVorgang | null {
  const e = sitzung.get(kassenId);
  return e?.ungeklaert !== undefined ? e.ungeklaert : ladeUngeklaerteBuchung(speicher, kassenId);
}

/** Laufende Kassenbuch-Anfrage dieser Kasse (auch aus einer inzwischen verlassenen Ansicht). */
export function laufendeBuchung(kassenId: string): Promise<void> | null {
  return sitzung.get(kassenId)?.lauf ?? null;
}

/** Kennung schon gemerkt (ungeklärt im Register bzw. Speicher oder als erledigt markiert)? */
function istBekannt(speicher: Speicher, kassenId: string, vorgangId: string): boolean {
  if (eintrag(kassenId).ungeklaert?.vorgangId === vorgangId) {
    return true;
  }
  try {
    const roh = speicher.getItem(schluessel(kassenId));
    return roh?.includes(JSON.stringify(vorgangId)) === true;
  } catch {
    return false; // unlesbarer Speicher: `merke` scheitert ohnehin, es wird nicht gesendet
  }
}

/** Vor dem Senden merken und zurücklesen; `false` = Speicher nicht beschreibbar. */
function merke(speicher: Speicher, kassenId: string, v: KassenbuchVorgang): boolean {
  try {
    speicher.setItem(schluessel(kassenId), JSON.stringify(v));
    return ladeUngeklaerteBuchung(speicher, kassenId)?.vorgangId === v.vorgangId;
  } catch {
    return false;
  }
}

/**
 * Ausgang steht fest: Merker entfernen. Scheitert das Entfernen, wird die Buchung als erledigt
 * überschrieben, damit sie nach dem Neuladen nicht wieder als ungeklärt erscheint (Codex R2).
 */
function vergiss(speicher: Speicher, kassenId: string, vorgangId: string): void {
  const k = schluessel(kassenId);
  try {
    if (ladeUngeklaerteBuchung(speicher, kassenId)?.vorgangId !== vorgangId) {
      return;
    }
    speicher.removeItem(k);
  } catch {
    try {
      speicher.setItem(k, JSON.stringify({ erledigt: vorgangId }));
    } catch {
      // Speicher vollständig gesperrt: Dieses Fenster weiß es (Sitzungsregister); nach dem
      // Neuladen fragt „erneut senden“ zuerst nach dem Kassenbuch
    }
  }
}

/**
 * Buchung senden: vorher merken (sonst nicht senden), als laufend eintragen, danach den
 * Ausgang festhalten – erledigt (2xx, 4xx) oder ungeklärt (Netz, Zeitüberschreitung, 5xx).
 * Bei einer Wiederholung erledigt nur 2xx den Vorgang; jede Abweisung lässt ihn ungeklärt.
 * Eine zweite Buchung derselben Kasse während einer laufenden wird abgewiesen.
 */
export function sendeKassenbuchVorgang<T>(
  speicher: Speicher,
  kassenId: string,
  v: KassenbuchVorgang,
  /**
   * Versand; `wiederholung` = bekannte Kennung. Wiederholungen müssen an die eigene Route gehen
   * (…/kassenbuch/wiederholung), die ein älterer Server nicht kennt (E-W1-RKSV F2).
   */
  senden: (v: KassenbuchVorgang, wiederholung: boolean) => Promise<T>,
  /**
   * E-W1-RKSV F2: vor einer Wiederholung (bekannte Kennung) befragt; nur `true` erlaubt den
   * Versand. Fehlt die Prüfung, wird nie wiederholt.
   */
  serverErkenntWiederholung?: () => Promise<boolean>,
): Promise<T> {
  const e = eintrag(kassenId);
  if (e.lauf !== null) {
    return Promise.reject(new Error('Kassenbuch-Buchung läuft bereits'));
  }
  const wiederholung = istBekannt(speicher, kassenId, v.vorgangId);
  // Codex W2-A2 R4: auch unabhängig von der Oberfläche – eine bereits gemerkte Kennung geht bei
  // ausgeschaltetem Schalter nie ein zweites Mal hinaus (ein Server vor 17f2d82 bucht sie neu).
  if (wiederholung && !KASSENBUCH_SCHALTER.wiederholungAktiv) {
    return Promise.reject(new KassenbuchWiederholungGesperrt());
  }
  if (!merke(speicher, kassenId, v)) {
    return Promise.reject(new KassenbuchSpeicherFehler());
  }
  const ergebnis = (async () => {
    try {
      // Codex R3: erst nach dem Eintragen des Laufs aufrufen – auch ein synchron werfender
      // Aufruf läuft so durch `finally` und hinterlässt keine Dauersperre
      await Promise.resolve();
      // E-W1-RKSV F2 (Rollback-Untergrenze): Wiederholung nur, wenn der Server jetzt bestätigt,
      // dass er die Kennung erkennt – unter dem Lauf dieser Kasse, unmittelbar vor dem Versand
      if (wiederholung && !(await pruefe(serverErkenntWiederholung))) {
        throw new KassenbuchWiederholungGesperrt();
      }
      const r = await senden(v, wiederholung);
      e.ungeklaert = null;
      vergiss(speicher, kassenId, v.vorgangId);
      return r;
    } catch (fehler: unknown) {
      if (fehler instanceof KassenbuchWiederholungGesperrt) {
        // nicht gesendet – die Buchung bleibt ungeklärt, der Merker bleibt
        e.ungeklaert = v;
        throw fehler;
      }
      if (wiederholung && fehler instanceof ApiFehler && fehler.status === 404) {
        // Wiederholungsroute unbekannt: Server unter der Rollback-Untergrenze – nichts gebucht,
        // aber die ursprüngliche Buchung bleibt ungeklärt (nie als „nicht gebucht“ werten)
        e.ungeklaert = v;
        throw new KassenbuchWiederholungGesperrt();
      }
      if (wiederholung) {
        // Codex E-W1-RKSV R2: Eine abgewiesene Wiederholung (z. B. 403 nach abgelaufener
        // Bedienersitzung) sagt nichts darüber, ob die ursprüngliche Buchung ankam – der Merker
        // bleibt, bis eine Wiederholung gelingt oder der Bediener ausdrücklich verwirft
        e.ungeklaert = v;
        throw fehler;
      }
      if (ausgangUnklar(fehler)) {
        e.ungeklaert = v;
      } else {
        e.ungeklaert = null;
        vergiss(speicher, kassenId, v.vorgangId);
      }
      throw fehler;
    } finally {
      // vor dem Abschluss von `ergebnis`: Wer darauf wartet, sieht keine laufende Buchung mehr
      e.lauf = null;
    }
  })();
  e.lauf = ergebnis.then(
    () => undefined,
    () => undefined,
  );
  return ergebnis;
}

async function pruefe(f: (() => Promise<boolean>) | undefined): Promise<boolean> {
  if (f === undefined) {
    return false;
  }
  try {
    return await f();
  } catch {
    return false;
  }
}

/** Ungeklärte Buchung ausdrücklich verwerfen (gebucht bzw. sicher nicht angekommen). */
export function verwirfKassenbuchVorgang(
  speicher: Speicher,
  kassenId: string,
  vorgangId: string,
): void {
  const e = eintrag(kassenId);
  if (e.lauf !== null) {
    return;
  }
  e.ungeklaert = null;
  vergiss(speicher, kassenId, vorgangId);
}

/**
 * Ausgang der Buchung unklar? Netzfehler, Zeitüberschreitung (Status 0) und Serverfehler (5xx)
 * – der Server kann gebucht haben. Eine Ablehnung (4xx) ist eindeutig: nicht gebucht.
 */
export function ausgangUnklar(e: unknown): boolean {
  if (e instanceof ApiFehler) {
    return e.status === 0 || e.status >= 500;
  }
  return true;
}
